import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  FileText, Upload, Download, Trash2, Eye, Lock, Calendar,
  Building2, Gift, Tag, Shield, ShieldAlert, ShieldCheck,
  Search, Filter, FolderOpen, File, RefreshCw, Plus
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const documentTypes = [
  { value: "convention", label: "Convention", icon: FileText },
  { value: "contrat", label: "Contrat", icon: FileText },
  { value: "conditions", label: "Conditions particulières", icon: FileText },
  { value: "interne", label: "Document interne", icon: Lock },
  { value: "justificatif", label: "Justificatif", icon: File },
  { value: "autre", label: "Autre", icon: FolderOpen },
];

const confidentialityLevels = [
  { value: "standard", label: "Standard", icon: Shield, color: "bg-gray-100 text-gray-700" },
  { value: "confidentiel", label: "Confidentiel", icon: ShieldAlert, color: "bg-yellow-100 text-yellow-700" },
  { value: "tres_confidentiel", label: "Très confidentiel", icon: ShieldCheck, color: "bg-red-100 text-red-700" },
];

export default function AdminDocumentsPage() {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  const [documents, setDocuments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const [filterType, setFilterType] = useState("all");
  const [filterPartner, setFilterPartner] = useState("all");
  const [filterConfidentiality, setFilterConfidentiality] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [uploadForm, setUploadForm] = useState({
    file: null,
    title: "",
    document_type: "autre",
    description: "",
    partner_id: "",
    deal_id: "",
    confidentiality_level: "standard",
    expiration_date: "",
    tags: "",
  });

  useEffect(() => {
    loadDocuments();
    loadPartners();
    loadDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterPartner, filterConfidentiality]);

  const loadDocuments = async () => {
    try {
      let url = `${API}/api/admin/documents`;
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("document_type", filterType);
      if (filterPartner !== "all") params.append("partner_id", filterPartner);
      if (filterConfidentiality !== "all") params.append("confidentiality_level", filterConfidentiality);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Impossible de charger les documents");
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${API}/api/partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(res.data);
    } catch (error) {
      console.error("Error loading partners:", error);
    }
  };

  const loadDeals = async () => {
    try {
      const res = await axios.get(`${API}/api/deals?include_drafts=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(res.data);
    } catch (error) {
      console.error("Error loading deals:", error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({
        ...uploadForm,
        file,
        title: uploadForm.title || file.name.replace(/\.[^/.]+$/, ""),
      });
    }
  };

  const uploadDocument = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      toast({ title: "Erreur", description: "Veuillez sélectionner un fichier et entrer un titre", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("title", uploadForm.title);
      formData.append("document_type", uploadForm.document_type);
      formData.append("description", uploadForm.description);
      formData.append("confidentiality_level", uploadForm.confidentiality_level);
      
      if (uploadForm.partner_id) formData.append("partner_id", uploadForm.partner_id);
      if (uploadForm.deal_id) formData.append("deal_id", uploadForm.deal_id);
      if (uploadForm.expiration_date) formData.append("expiration_date", uploadForm.expiration_date);
      if (uploadForm.tags) formData.append("tags", uploadForm.tags);

      await axios.post(`${API}/api/admin/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Document uploadé ! Le document a été ajouté au coffre-fort.");
      setShowUploadModal(false);
      resetUploadForm();
      loadDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Impossible d'uploader le document");
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      file: null,
      title: "",
      document_type: "autre",
      description: "",
      partner_id: "",
      deal_id: "",
      confidentiality_level: "standard",
      expiration_date: "",
      tags: "",
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm("Supprimer ce document du coffre-fort ?")) return;
    
    try {
      await axios.delete(`${API}/api/admin/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Document supprimé");
      loadDocuments();
      setShowDetailModal(false);
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Impossible de supprimer le document");
    }
  };

  const getConfidentialityBadge = (level) => {
    const config = confidentialityLevels.find(l => l.value === level) || confidentialityLevels[0];
    return (
      <Badge className={`${config.color} gap-1`}>
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const config = documentTypes.find(t => t.value === type) || documentTypes[5];
    return (
      <Badge variant="outline" className="gap-1">
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredDocuments = documents.filter(doc => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doc.title?.toLowerCase().includes(query) ||
      doc.description?.toLowerCase().includes(query) ||
      doc.partner_name?.toLowerCase().includes(query) ||
      doc.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  // Stats
  const stats = {
    total: documents.length,
    conventions: documents.filter(d => d.document_type === "convention").length,
    contrats: documents.filter(d => d.document_type === "contrat").length,
    confidentiels: documents.filter(d => d.confidentiality_level !== "standard").length,
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="admin-documents-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
              <Lock className="h-6 w-6" />
              Coffre-Fort Documentaire
            </h1>
            <p className="text-muted-foreground mt-1">
              Documents confidentiels et partenariats
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadDocuments} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </Button>
            <Button onClick={() => setShowUploadModal(true)} className="bg-accent hover:bg-accent/90 gap-2">
              <Plus className="h-4 w-4" />
              Ajouter un document
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-primary">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Documents</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{stats.conventions}</p>
              <p className="text-sm text-muted-foreground">Conventions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-purple-700">{stats.contrats}</p>
              <p className="text-sm text-muted-foreground">Contrats</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-700">{stats.confidentiels}</p>
              <p className="text-sm text-red-600">Confidentiels</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher..."
              className="pl-10"
            />
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {documentTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterPartner} onValueChange={setFilterPartner}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Partenaire" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les partenaires</SelectItem>
              {partners.map((partner) => (
                <SelectItem key={partner.id} value={partner.id}>{partner.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterConfidentiality} onValueChange={setFilterConfidentiality}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Confidentialité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous niveaux</SelectItem>
              {confidentialityLevels.map((level) => (
                <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Documents Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Chargement...</div>
        ) : filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun document trouvé</p>
              <Button 
                onClick={() => setShowUploadModal(true)} 
                className="mt-4 bg-accent hover:bg-accent/90"
              >
                Ajouter le premier document
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <Card key={doc.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
                setSelectedDocument(doc);
                setShowDetailModal(true);
              }}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FileText className="h-6 w-6 text-primary" />
                    </div>
                    {getConfidentialityBadge(doc.confidentiality_level)}
                  </div>
                  
                  <h3 className="font-semibold text-primary mb-1 line-clamp-1">{doc.title}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{doc.file_name}</p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {getTypeBadge(doc.document_type)}
                    {doc.partner_name && (
                      <Badge variant="outline" className="gap-1">
                        <Building2 className="h-3 w-3" />
                        {doc.partner_name}
                      </Badge>
                    )}
                  </div>
                  
                  {doc.tags && doc.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {doc.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ajouter un document</DialogTitle>
            <DialogDescription>
              Uploader un document dans le coffre-fort sécurisé
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium mb-2">Fichier *</label>
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-accent transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                />
                {uploadForm.file ? (
                  <div>
                    <File className="h-8 w-8 mx-auto text-accent mb-2" />
                    <p className="font-medium">{uploadForm.file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(uploadForm.file.size)}</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Cliquez ou glissez un fichier</p>
                  </div>
                )}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Titre *</label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="Titre du document"
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-1">Type de document</label>
              <Select 
                value={uploadForm.document_type} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, document_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Confidentiality */}
            <div>
              <label className="block text-sm font-medium mb-1">Niveau de confidentialité</label>
              <Select 
                value={uploadForm.confidentiality_level} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, confidentiality_level: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {confidentialityLevels.map((level) => (
                    <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Partner */}
            <div>
              <label className="block text-sm font-medium mb-1">Rattacher à un partenaire</label>
              <Select 
                value={uploadForm.partner_id || "all"} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, partner_id: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Aucun</SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>{partner.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Deal */}
            <div>
              <label className="block text-sm font-medium mb-1">Rattacher à une offre</label>
              <Select 
                value={uploadForm.deal_id || "all"} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, deal_id: v === "all" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Aucune" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Aucune</SelectItem>
                  {deals.map((deal) => (
                    <SelectItem key={deal.id} value={deal.id}>{deal.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Description du document..."
                rows={2}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-1">Tags (séparés par virgule)</label>
              <Input
                value={uploadForm.tags}
                onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })}
                placeholder="partenariat, 2024, MSC..."
              />
            </div>

            {/* Expiration */}
            <div>
              <label className="block text-sm font-medium mb-1">Date d'expiration</label>
              <Input
                type="date"
                value={uploadForm.expiration_date}
                onChange={(e) => setUploadForm({ ...uploadForm, expiration_date: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowUploadModal(false); resetUploadForm(); }}>
              Annuler
            </Button>
            <Button 
              onClick={uploadDocument} 
              disabled={uploading || !uploadForm.file || !uploadForm.title}
              className="bg-accent hover:bg-accent/90"
            >
              {uploading ? "Upload en cours..." : "Uploader"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          {selectedDocument && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  {getTypeBadge(selectedDocument.document_type)}
                  {getConfidentialityBadge(selectedDocument.confidentiality_level)}
                </div>
                <DialogTitle>{selectedDocument.title}</DialogTitle>
                <DialogDescription>{selectedDocument.file_name}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {selectedDocument.description && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Description</p>
                    <p className="text-sm">{selectedDocument.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Taille</p>
                    <p className="text-sm">{formatFileSize(selectedDocument.file_size)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Date d'ajout</p>
                    <p className="text-sm">{new Date(selectedDocument.created_at).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
                
                {selectedDocument.partner_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Partenaire</p>
                    <p className="text-sm flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {selectedDocument.partner_name}
                    </p>
                  </div>
                )}
                
                {selectedDocument.deal_title && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Offre associée</p>
                    <p className="text-sm flex items-center gap-1">
                      <Gift className="h-4 w-4" />
                      {selectedDocument.deal_title}
                    </p>
                  </div>
                )}
                
                {selectedDocument.expiration_date && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Date d'expiration</p>
                    <p className="text-sm flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(selectedDocument.expiration_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                
                {selectedDocument.tags && selectedDocument.tags.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedDocument.tags.map((tag, i) => (
                        <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <a 
                  href={selectedDocument.file_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex"
                >
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-1" />
                    Télécharger
                  </Button>
                </a>
                <Button
                  variant="destructive"
                  onClick={() => deleteDocument(selectedDocument.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Supprimer
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
